import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Search, X, Calendar } from 'lucide-react';
import axios from 'axios';
import { baseURL } from '../../../../Services/api/baseURL';

const VariantSalesChart = ({ storeId}) => {
  const [salesData, setSalesData] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const now = new Date()
  const currentMonth = now.getMonth()+1

  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(2025);
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [error, setError] = useState('');

  // API CALL using axios - This is where the actual API request happens
  const fetchSalesData = async () => {
    setLoading(true);
    setError('');
    const token = JSON.parse(localStorage.getItem("token"));
    
    const apiUrl = `${baseURL}/api/order/get-sold-variant-monthly-history/${storeId}`;
    
    try {
      const response = await axios.get(apiUrl, { headers: { Authorization: token },
        params: {
          month: month,
          year: year
        },
        // timeout: 10000 // 10 second timeout
      });
      
      console.log('Axios response received:', response.data);
      
      if (response.data.success && response.data.data) {
        console.log('Setting sales data:', response.data.data.length, 'items');
        setSalesData(response.data.data);
      } else {
        setError(response.data.message || 'No data received from API');
        setSalesData([]);
      }
    } catch (error) {
      console.error('Axios call failed:', error);
      
      if (error.response) {
        // Server responded with error status
        setError(`Server Error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        // Request made but no response
        setError('Network Error: No response from server. Check if API is running.');
      } else {
        // Something else happened
        setError(`Request Error: ${error.message}`);
      }
      setSalesData([]);
    } finally {
      setLoading(false);
    }
  };

  // Trigger API call when component mounts or when month/year changes
  useEffect(() => {
    fetchSalesData();
  }, [month, year, storeId]);

  // Get unique variants from API data
  const uniqueVariants = useMemo(() => {
    const variantMap = new Map();
    salesData.forEach(item => {
      if (!variantMap.has(item.variantId)) {
        variantMap.set(item.variantId, {
          variantId: item.variantId,
          variantName: item.variantName,
          productName: item.productName,
          category_name: item.category_name,
          subcategory_name: item.subcategory_name
        });
      }
    });
    return Array.from(variantMap.values());
  }, [salesData]);

  // Filter variants based on search term
  const filteredVariants = useMemo(() => {
    if (!searchTerm) return uniqueVariants;
    return uniqueVariants.filter(variant =>
      variant.variantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variant.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variant.category_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [uniqueVariants, searchTerm]);

  // Process API data for chart
  const chartData = useMemo(() => {
    if (selectedVariants.length === 0) return [];

    const allDates = [...new Set(salesData.map(item => item.date))].sort();
    
    return allDates.map(date => {
      const dayData = { date };
      
      selectedVariants.forEach(variantId => {
        const variant = uniqueVariants.find(v => v.variantId === variantId);
        const salesForDay = salesData.find(item => 
          item.date === date && item.variantId === variantId
        );
        
        dayData[variant?.variantName || variantId] = salesForDay ? salesForDay.totalSold : 0;
      });
      
      return dayData;
    });
  }, [salesData, selectedVariants, uniqueVariants]);

  const generateColors = (count) => {
    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff88',
      '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7',
      '#dda0dd', '#f0b27a', '#85c1e9', '#f8c471', '#bb8fce'
    ];
    return colors.slice(0, count);
  };

  const handleVariantToggle = (variantId) => {
    setSelectedVariants(prev => {
      if (prev.includes(variantId)) {
        return prev.filter(id => id !== variantId);
      } else {
        return [...prev, variantId];
      }
    });
  };

  const removeVariant = (variantId) => {
    setSelectedVariants(prev => prev.filter(id => id !== variantId));
  };

  const clearAllSelections = () => {
    setSelectedVariants([]);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-700 mb-2">Date: {label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {`${entry.dataKey}: ${entry.value} sold`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
//   };
const formatDate = (dateString) => {
  const [year, month, day] = dateString.split("-");
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${monthNames[parseInt(month)-1]} ${parseInt(day)}`;
};

  return (
    <div className="w-full max-w-7xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Variant Sales Comparison</h2>
        <p className="text-gray-600">Compare daily sales performance across different product variants</p>
      </div>

      <div className="mb-6 space-y-4">
        {loading && (
          <div className="flex items-center justify-center p-4 bg-blue-50 rounded-lg">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-blue-700">Fetching data from API...</span>
          </div>
        )}
        
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
            <button 
              onClick={fetchSalesData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Retry API Call
            </button>
          </div>
        )}

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-6 h-6  text-gray-500" />
            <label className="text-sm  my-auto font-medium text-gray-700">Month:</label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              disabled={loading}
              className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleDateString('en-US', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium my-auto text-gray-700">Year:</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              disabled={loading}
              className="px-3 py-1 w-20 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>
        </div>

        <div className="relative">
          <div className="flex items-center space-x-2 mb-2">
            <label className="text-sm font-medium text-gray-700">Select Variants to Compare:</label>
            {selectedVariants.length > 0 && (
              <button
                onClick={clearAllSelections}
                className="text-sm text-red-600 hover:text-red-800 underline"
              >
                Clear All
              </button>
            )}
          </div>
          
<div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search variants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsDropdownOpen(true)}
              style={{ paddingLeft: '3rem' }}
              className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {selectedVariants.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedVariants.map(variantId => {
                const variant = uniqueVariants.find(v => v.variantId === variantId);
                return (
                  <div key={variantId} className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    <span className="truncate max-w-48">{variant?.variantName}</span>
                    <button
                      onClick={() => removeVariant(variantId)}
                      className="ml-2 hover:text-blue-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {isDropdownOpen && (
            <div className="absolute z-10 w-full max-h-60 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg">
              {filteredVariants.map(variant => (
                <div
                  key={variant.variantId}
                  className={`p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 ${
                    selectedVariants.includes(variant.variantId) ? 'bg-blue-50 text-blue-700' : ''
                  }`}
                  onClick={() => handleVariantToggle(variant.variantId)}
                >
                  <div className="font-medium text-sm">{variant.variantName}</div>
                  <div className="text-xs text-gray-500">
                    {variant.category_name} - {variant.subcategory_name}
                  </div>
                </div>
              ))}
              {filteredVariants.length === 0 && (
                <div className="p-3 text-gray-500 text-sm">
                  {salesData.length === 0 ? 'No data from API' : 'No variants found'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}

      <div className="h-96 w-full">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-lg">Loading from API...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg mb-2 text-red-600">API Call Failed</p>
              <p className="text-sm mb-4">{error}</p>
              <button 
                onClick={fetchSalesData}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Retry API Call
              </button>
            </div>
          </div>
        ) : selectedVariants.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg mb-2">Select variants to compare their sales</p>
              <p className="text-sm">
                {salesData.length === 0 
                  ? 'No sales data available from API for the selected period' 
                  : `${salesData.length} records loaded from API`
                }
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis label={{ value: 'Units Sold', angle: -90, position: 'insideLeft' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {selectedVariants.map((variantId, index) => {
                const variant = uniqueVariants.find(v => v.variantId === variantId);
                const colors = generateColors(selectedVariants.length);
                return (
                  <Line
                    key={variantId}
                    type="monotone"
                    dataKey={variant?.variantName || variantId}
                    stroke={colors[index]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {selectedVariants.length > 0 && salesData.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {selectedVariants.map(variantId => {
            const variant = uniqueVariants.find(v => v.variantId === variantId);
            const totalSales = salesData
              .filter(item => item.variantId === variantId)
              .reduce((sum, item) => sum + item.totalSold, 0);
            
            return (
              <div key={variantId} className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-sm text-gray-800 mb-1 truncate">
                  {variant?.variantName}
                </h4>
                <p className="text-2xl font-bold text-blue-600">{totalSales}</p>
                <p className="text-xs text-gray-500">Total Units Sold</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VariantSalesChart;